import { useMemo, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@bravemove/design-tokens/icons';
import { cn } from '../../utils/cn';

export interface DateRange {
  readonly start?: string | undefined;
  readonly end?: string | undefined;
}

export interface DateRangePickerProps {
  readonly value: DateRange;
  readonly onChange: (range: DateRange) => void;
  readonly min?: string;
  readonly max?: string;
  readonly locale?: string;
  readonly weekStartsOn?: 0 | 1;
  readonly className?: string;
}

function parseISO(iso: string | undefined): Date | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}
function toISO(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}
function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
function inRange(d: Date, start: Date, end: Date): boolean {
  return d >= start && d <= end;
}

export function DateRangePicker({
  value,
  onChange,
  min,
  max,
  locale = 'en-US',
  weekStartsOn = 1,
  className,
}: DateRangePickerProps) {
  const startDate = useMemo(() => parseISO(value.start), [value.start]);
  const endDate = useMemo(() => parseISO(value.end), [value.end]);
  const [view, setView] = useState<Date>(() => startDate ?? new Date());
  const [hover, setHover] = useState<Date | null>(null);
  const minDate = useMemo(() => parseISO(min), [min]);
  const maxDate = useMemo(() => parseISO(max), [max]);

  const weekdayLabels = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(locale, { weekday: 'short' });
    return Array.from({ length: 7 }, (_, i) =>
      fmt.format(new Date(2024, 0, ((i + weekStartsOn) % 7) + 7)),
    );
  }, [locale, weekStartsOn]);

  const monthLabel = useMemo(
    () => new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(view),
    [view, locale],
  );

  const grid = useMemo(() => {
    const first = startOfMonth(view);
    const firstWeekday = first.getDay();
    const offset = (firstWeekday - weekStartsOn + 7) % 7;
    const start = new Date(first);
    start.setDate(start.getDate() - offset);
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  }, [view, weekStartsOn]);

  const isDisabled = (d: Date): boolean => {
    if (minDate && d < new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate()))
      return true;
    if (maxDate && d > new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate()))
      return true;
    return false;
  };

  const handleClick = (d: Date) => {
    if (isDisabled(d)) return;
    const iso = toISO(d);
    if (!startDate || (startDate && endDate)) {
      onChange({ start: iso });
      return;
    }
    if (d < startDate) {
      onChange({ start: iso, end: value.start });
      return;
    }
    onChange({ start: value.start, end: iso });
  };

  const previewEnd = !endDate && startDate && hover && hover >= startDate ? hover : null;

  return (
    <div
      className={cn(
        'inline-block rounded-xl bg-surface border border-hairline p-3 select-none',
        className,
      )}
    >
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          aria-label="Previous month"
          onClick={() => setView((v) => addMonths(v, -1))}
          className="size-7 inline-flex items-center justify-center rounded-md text-ink-2 hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-accent/25 rtl:rotate-180"
        >
          <ChevronLeftIcon size={14} color="currentColor" />
        </button>
        <div aria-live="polite" className="text-[13px] font-bold text-ink">
          {monthLabel}
        </div>
        <button
          type="button"
          aria-label="Next month"
          onClick={() => setView((v) => addMonths(v, 1))}
          className="size-7 inline-flex items-center justify-center rounded-md text-ink-2 hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-accent/25 rtl:rotate-180"
        >
          <ChevronRightIcon size={14} color="currentColor" />
        </button>
      </div>
      <div className="grid grid-cols-7 text-[10px] text-ink-3 font-mono uppercase tracking-wider mb-1">
        {weekdayLabels.map((d, i) => (
          <div key={i} className="size-9 inline-flex items-center justify-center">
            {d}
          </div>
        ))}
      </div>
      <div
        role="grid"
        aria-label={monthLabel}
        onMouseLeave={() => setHover(null)}
        className="grid grid-cols-7"
      >
        {grid.map((d) => {
          const inMonth = d.getMonth() === view.getMonth();
          const disabled = isDisabled(d);
          const isStart = startDate ? isSameDay(d, startDate) : false;
          const isEnd = endDate ? isSameDay(d, endDate) : false;
          const isInRange = startDate && endDate ? inRange(d, startDate, endDate) : false;
          const isInPreview =
            startDate && !endDate && previewEnd ? inRange(d, startDate, previewEnd) : false;

          return (
            <button
              key={d.getTime()}
              type="button"
              role="gridcell"
              aria-selected={isStart || isEnd || isInRange}
              disabled={disabled}
              onMouseEnter={() => setHover(d)}
              onClick={() => handleClick(d)}
              className={cn(
                'size-9 inline-flex items-center justify-center text-[12.5px] tabular-nums',
                'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-accent/25',
                'transition-colors duration-[100ms]',
                inMonth ? 'text-ink' : 'text-ink-3',
                (isInRange || isInPreview) && !isStart && !isEnd && 'bg-accent-soft',
                isStart && 'bg-primary text-primary-ink font-bold rounded-s-md',
                isEnd && 'bg-primary text-primary-ink font-bold rounded-e-md',
                !isStart &&
                  !isEnd &&
                  !isInRange &&
                  !isInPreview &&
                  !disabled &&
                  'hover:bg-surface-2 rounded-md',
                disabled && 'opacity-30 cursor-not-allowed',
              )}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}
